import { fromUnixTime, isSameHour } from 'date-fns';
import { MESSAGE_TYPE } from 'shared/constants/messages';

/**
 * Gets the user ID from a message (from meta_data or sender)
 * @param {Object} message - Message object
 * @returns {number|null} - User ID or null
 */
const getUserId = message => {
  // Try meta_data first (new activity messages)
  if (message.meta_data?.performed_by?.id) {
    return message.meta_data.performed_by.id;
  }
  // Fallback to sender
  return message.sender?.id || null;
};

/**
 * Groups consecutive activity messages from the same user within the same hour
 * @param {Array} messages - Array of message objects
 * @returns {Array} - Array of items (either single messages or grouped messages)
 */
export const groupActivityMessages = messages => {
  if (!messages || messages.length === 0) return [];

  const result = [];
  let currentGroup = null;

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    const isActivityMessage = message.message_type === MESSAGE_TYPE.ACTIVITY;

    if (isActivityMessage) {
      // Check if we should add to current group
      if (currentGroup && shouldAddToGroup(message, currentGroup)) {
        currentGroup.messages.push(message);
      } else {
        // Finalize current group if it exists
        if (currentGroup) {
          finalizeGroup(currentGroup, result);
        }
        // Start new group
        currentGroup = {
          messages: [message],
          userId: getUserId(message),
          firstTimestamp: message.created_at,
        };
      }
    } else {
      // Non-activity message - finalize current group and add regular message
      if (currentGroup) {
        finalizeGroup(currentGroup, result);
        currentGroup = null;
      }
      result.push(message);
    }
  }

  // Finalize last group if exists
  if (currentGroup) {
    finalizeGroup(currentGroup, result);
  }

  return result;
};

/**
 * Checks if a message should be added to the current group
 * @param {Object} message - Message to check
 * @param {Object} group - Current group object
 * @returns {boolean}
 */
const shouldAddToGroup = (message, group) => {
  // Check same user
  const messageUserId = getUserId(message);
  const sameUser = messageUserId && messageUserId === group.userId;
  if (!sameUser) return false;

  // Check same hour
  const messageTime = fromUnixTime(message.created_at);
  const groupTime = fromUnixTime(group.firstTimestamp);
  const sameHour = isSameHour(messageTime, groupTime);

  return sameHour;
};

/**
 * Finalizes a group by adding it to result array
 * Either as a group (if 2+ messages) or as individual messages
 * @param {Object} group - Group object
 * @param {Array} result - Result array to push to
 */
const finalizeGroup = (group, result) => {
  if (group.messages.length >= 2) {
    // Add as a group
    result.push({
      isGroup: true,
      messages: group.messages,
      id: `group-${group.messages[0].id}`, // Unique ID for v-for key
    });
  } else {
    // Add as individual message
    result.push(group.messages[0]);
  }
};
